import React, { useState } from "react";
import InitialAssesmentTimeAreaLineChart from "./InitialAssesmentTimeAreaLineChart";
import TriageTimeAreaChart from "./TriageTimeAreaChart";
import DoorToneedleTimeLineChart from "./DoorToneedleTimeLineChart";
import DoorToCTTimelineChart from "./DoorToCTTimelineChart";
import DoorToECGAreaChart from "./DoorToECGAreaChart";
import DoorToNeedleStrokeChart from "./DoorToNeedleStrokeChart";
import DoorToNeedleCompliance from "./DoorToNeedleCompliance";
import ERPatientListmodal from "./ERPatientListmodal";
import CommonButton from "../../common/components/Buttons/CommonButton";
import CommonDivider from "../../common/components/divider/CommonDivider";

const TOP_KPI_CARDS = [
  {
    id: "triage",
    title: "Avg Time to Initial Assessment",
    value: 7.2,
    unit: "min",
    target: 10, // <= 10 min is good
    type: "time",
    gradient: "from-blue-50 to-blue-200",
    iconBg: "bg-blue-600",
    icon: (
      <svg
        viewBox="0 0 24 24"
        className="w-7 h-7 text-white"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v6l4 2" />
      </svg>
    ),
  },

  {
    id: "returnRate",
    title: "72-Hour ER Return Rate",
    value: 4.5,
    unit: "%",
    target: 5, // lower is better
    type: "inverse",
    gradient: "from-orange-50 via-orange-100 to-red-200",
    iconBg: "bg-orange-600",
    icon: (
      <svg
        viewBox="0 0 24 24"
        className="w-7 h-7 text-white"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M3 12a9 9 0 1 0 3-6.7" />
        <path d="M3 4v6h6" />
      </svg>
    ),
  },

  {
    id: "needleCompliance",
    title: "Door-to-Needle Compliance",
    value: 86,
    unit: "%",
    target: 90,
    type: "percentage",
    gradient: "from-green-50 via-green-100 to-green-300",
    iconBg: "bg-green-700",
    icon: (
      <svg
        viewBox="0 0 24 24"
        className="w-7 h-7 text-white"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M4 20L20 4" />
        <path d="M15 5l4 4" />
        <path d="M9 15l-4-4" />
      </svg>
    ),
  },

  {
    id: "poct",
    title: "POCT Result Availability",
    value: 92,
    unit: "%",
    target: 95,
    type: "percentage",
    gradient: "from-blue-50 via-blue-100 to-blue-300",
    iconBg: "bg-blue-600",
    icon: (
      <svg
        viewBox="0 0 24 24"
        className="w-7 h-7 text-white"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <rect x="5" y="3" width="14" height="18" rx="2" />
        <path d="M9 7h6M9 12h6M9 17h4" />
      </svg>
    ),
  },

  // ✅ NEW KPI – Hypoglycemia
  {
    id: "hypoglycemia",
    title: "Hypoglycemia Timely Management Rate",
    value: 68,
    unit: "%",
    target: 90,
    type: "percentage",
    gradient: "from-purple-50 via-purple-100 to-purple-300",
    iconBg: "bg-purple-700",
    icon: (
      <svg
        viewBox="0 0 24 24"
        className="w-7 h-7 text-white"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M12 2v20" />
        <path d="M7 7h10" />
        <path d="M7 17h10" />
      </svg>
    ),
  },
];


const getStatus = (card) => {
  if (card.type === "time" || card.type === "inverse") {
    if (card.value <= card.target) return "good";
    if (card.value <= card.target + 2) return "warning";
    return "critical";
  }

  if (card.value >= card.target) return "good";
  if (card.value >= card.target - 10) return "warning";
  return "critical";
};

const STATUS_GRADIENT = {
  good: "from-green-300 via-green-400 to-green-700",
  warning: "from-yellow-300 via-yellow-400 to-yellow-600",
  critical: "from-red-300 via-red-400 to-red-600",
};

 
function ERKPIDashboard() {
  const [openErPatientListModal, setOpenErPatientListModal] = useState(false);
  return (
    <div className="px-2">
      <div className="flex justify-between  items-center w-full mt-16 pl-5 pr-3">
        <h1 className="font-semibold text-center text-2xl">{"ER KPIs"}</h1>
        <div>
          <CommonButton
            label={"ER Patient TAT"}
            className={"bg-customBlue text-white "}
            onClick={() => setOpenErPatientListModal(true)}
          />
        </div>
      </div>
      <div className="py-1">
        <CommonDivider color="#B3B3B3" height={1} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2  lg:grid-cols-3 xl:grid-cols-5 gap-3 items-center">
        {TOP_KPI_CARDS.map((card) => {
          const status = getStatus(card);

          return (
            <div
              key={card.id}
              className={`bg-gradient-to-br ${STATUS_GRADIENT[status]}
        rounded-2xl p-[3px] shadow-md`}
            >
              {/* Inner container */}
              <div className="bg-white/85 backdrop-blur rounded-2xl p-2 h-full flex flex-col">
                {/* HEADER – fixed height */}
                <div className="flex items-center gap-3 min-h-[80px]">
                  {/* Icon */}
                  <div
                    className={`${card.iconBg} rounded-xl p-3 shadow-md shrink-0`}
                  >
                    {card.icon}
                  </div>

                  {/* Title + Value */}
                  <div className="flex flex-col">
                    <p className="text-sm font-semibold text-gray-700 leading-snug line-clamp-2">
                      {card.title}
                    </p>
                    <div className="text-3xl font-bold text-gray-900 leading-tight">
                      {card.value}
                      <span className="text-xl ml-1">{card.unit}</span>
                    </div>
                  </div>
                </div>

                {/* Spacer pushes footer down */}
                <div className="flex-grow" />

                {/* FOOTER – always aligned */}
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200">
                  <span className="text-xs text-gray-900 whitespace-nowrap">
                    Target : {card.target}
                    {card.unit}
                  </span>

                  <span
                    className={`text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap
                ${
                  status === "good"
                    ? "bg-green-200 text-green-900"
                    : status === "warning"
                      ? "bg-yellow-200 text-yellow-900"
                      : "bg-red-200 text-red-900"
                }`}
                  >
                    {status === "good" && "On Track"}
                    {status === "warning" && "Needs Attention"}
                    {status === "critical" && "Critical"}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="py-1">
        <CommonDivider color="#B3B3B3" height={1} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2  xl:grid-cols-3 gap-3 items-center">
        <div className="bg-white rounded-xl p-3 shadow-xl border">
          <h3 className="text-blue-900 font-bold text-base mb-3">
            Time Taken For Initial Assessment
          </h3>
          <div className=" h-[220px]">
            <InitialAssesmentTimeAreaLineChart />
          </div>
        </div>
        <div className="bg-white rounded-xl p-3 shadow-xl border">
          <h3 className="text-blue-900 font-bold text-base mb-3">
            Time to Triage
          </h3>
          <div className=" h-[220px]">
            <TriageTimeAreaChart />
          </div>
        </div>
        <div className="bg-white rounded-xl p-3 shadow-xl border">
          <h3 className="text-blue-900 font-bold text-base mb-3">Door to CT</h3>
          <div className=" h-[220px]">
            <DoorToCTTimelineChart />
          </div>
        </div>
        <div className="bg-white rounded-xl p-3 shadow-xl border">
          <h3 className="text-blue-900 font-bold text-base mb-3">
            Door to ECG
          </h3>
          <div className=" h-[220px]">
            <DoorToECGAreaChart />
          </div>
        </div>
        <div className="bg-white rounded-xl p-3 shadow-xl border">
          <h3 className="text-blue-900 font-bold text-base mb-3">
            Door-to-Needle (Acute Ischemic Stroke)
          </h3>
          <div className=" h-[220px]">
            <DoorToNeedleStrokeChart />
          </div>
        </div>
        <div className="bg-white rounded-xl p-3 shadow-xl border">
          <h3 className="text-blue-900 font-bold text-base mb-3">
            Door-to-Needle Compliance
          </h3>
          <div className=" h-[220px]">
            <DoorToNeedleCompliance />
          </div>
        </div>
      </div>
      {openErPatientListModal && (
        <ERPatientListmodal
          openErPatientListModal={openErPatientListModal}
          setOpenErPatientListModal={setOpenErPatientListModal}
        />
      )}
    </div>
  );
}

export default ERKPIDashboard;
